// Phase 6 — Cross-cutting "Publish to preset library" admin button.
//
// Single component used on every Plan-menu list page (8 entity types).
// Hides itself for non-admin users (the underlying server route would
// 403 anyway — this is just the UX gate). Hides itself for rows that
// are already presets (cannot publish twice).
//
// The component is presentational + a thin POST. The parent page is
// responsible for refreshing its row list once the publish resolves.
//
// Phase 6 polish (A.1): the affordance was previously rendered by
// SpinnerButton, which silently dropped the title/aria-label props,
// and several call sites passed label="" to make it icon-only. The
// reviewer reported the action was undiscoverable. We now switch to
// the shadcn Button primitive (forwards title + aria-label natively)
// and always render the "Publish" text next to the icon. Visibility
// gating is unchanged.
import { useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { admin as adminApi } from "@/api/endpoints";
import { formatApiError } from "@/lib/formatApiError";

export const PRESET_PUBLISH_ENTITY_TYPES = [
    "scenarios",
    "airframes",
    "environments",
    "engagements",
    "guidance-profiles",
    "threats",
    "terrain",
    "obstacles",
];

const A11Y_LABEL = "Publish to preset library";

/**
 * @param {object} props
 * @param {"scenarios"|"airframes"|"environments"|"engagements"|"guidance-profiles"|"threats"|"terrain"|"obstacles"} props.entityType
 * @param {string} props.entityId
 * @param {object} props.entity – the full row; used to read is_preset
 * @param {object} [props.user] – {role: "operator"|"admin"|"superadmin"}
 * @param {function} [props.onPublished] – fired with the response body
 * @param {string} [props.testId]
 * @param {"sm"|"default"|"lg"|"icon"} [props.size]
 * @param {"ghost"|"outline"|"default"|"secondary"} [props.variant]
 * @param {string} [props.label] – text rendered next to the icon. Empty
 *   strings now fall back to "Publish" so the affordance stays
 *   discoverable on every Plan list page.
 */
export default function PresetPublishButton({
    entityType,
    entityId,
    entity = null,
    user = null,
    onPublished,
    testId,
    size = "sm",
    variant = "ghost",
    label = "Publish",
}) {
    const [busy, setBusy] = useState(false);
    const role = (user?.role || "").toLowerCase();
    const isAdmin = role === "admin" || role === "superadmin";
    const alreadyPreset = !!(entity?.is_preset || entity?.summary?.is_preset);

    if (!isAdmin) return null;
    if (alreadyPreset) return null;
    if (!PRESET_PUBLISH_ENTITY_TYPES.includes(entityType)) return null;

    // Empty / falsy label → fall back to "Publish" so the row icon is
    // never rendered alone again. The reviewer flagged the icon-only
    // rendering as undiscoverable; we honour that across all callers.
    const displayLabel = (label || "").trim() || "Publish";

    const onPublish = async () => {
        setBusy(true);
        try {
            const res = await adminApi.publishToPresetLibrary(entityType, entityId);
            toast.success(
                `Published to preset library — id ${res?.published_id || "?"}`,
            );
            onPublished?.(res);
        } catch (e) {
            const detail = formatApiError(e, "Publish failed");
            toast.error(typeof detail === "string" ? detail : JSON.stringify(detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <Button
            type="button"
            size={size}
            variant={variant}
            disabled={busy}
            onClick={onPublish}
            title={A11Y_LABEL}
            aria-label={A11Y_LABEL}
            data-testid={testId || `publish-preset-${entityType}-${entityId}`}
        >
            {busy ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" aria-hidden="true" />
            ) : (
                <Upload className="h-4 w-4 mr-1.5" aria-hidden="true" />
            )}
            {displayLabel}
        </Button>
    );
}
